import { createContext, useState, useEffect } from 'react';
import API_BASE from '../utils/api';

export const AuthContext = createContext();

function getInitialAuth() {
  const accessToken = localStorage.getItem('accessToken');
  const refreshToken = localStorage.getItem('refreshToken');
  const idToken = localStorage.getItem('idToken');
  const expiresAt = localStorage.getItem('expiresAt');
  const onboardingStatus = localStorage.getItem('onboardingStatus');
  const plan = localStorage.getItem('plan');

  const isValid = refreshToken && expiresAt && Date.now() < Number(expiresAt);

  return {
    isHydrated: true,
    isLoggedIn: Boolean(isValid),
    accessToken: isValid ? accessToken : null,
    refreshToken: isValid ? refreshToken : null,
    idToken: isValid ? idToken : null,
    onboardingStatus: isValid ? onboardingStatus || 'pending' : null,
    plan: isValid ? plan || null : null,
  };
}

function isSuccessfulProfile(profile) {
  return profile && typeof profile === 'object' && 'onboardingStatus' in profile;
}

export default function AuthProvider({ children }) {
  const [auth, setAuth] = useState(() => {
    const initial = getInitialAuth();
    // A stored refresh token behind an expired access token is a session that
    // only needs refreshing. Hold the route guards on "Loading…" until
    // initialize() has tried, instead of bouncing to login while it is in flight
    // — which is what any reload more than an hour after sign-in used to do.
    const awaitingRefresh = !initial.isLoggedIn && Boolean(localStorage.getItem('refreshToken'));
    return { ...initial, isHydrated: !awaitingRefresh };
  });

  async function fetchProfile(idToken) {
    const res = await fetch(`${API_BASE}/api/auth/me`, {
      credentials: 'include',
      headers: idToken
        ? {
            Authorization: `Bearer ${idToken}`,
          }
        : undefined,
    });

    if (!res.ok) return null;
    return res.json();
  }

  function persistTokens(tokens) {
    localStorage.setItem('accessToken', tokens.accessToken);
    localStorage.setItem('idToken', tokens.idToken);
    localStorage.setItem('refreshToken', tokens.refreshToken);
    localStorage.setItem('expiresAt', Date.now() + tokens.expiresIn * 1000);
  }

  function persistEffectiveUserId(profile) {
    if (!profile) return;
    const ownUserId = profile.userId;
    const effectiveUserId = profile.effectiveUserId || ownUserId;
    if (ownUserId) {
      localStorage.setItem('ownUserId', ownUserId);
      // userId is used everywhere for data fetches — point it at the owner's data
      localStorage.setItem('userId', effectiveUserId);
    }
  }

  async function hydrateWithTokens(tokens) {
    persistTokens(tokens);
    const profile = await fetchProfile(tokens.idToken);
    const onboardingStatus = profile?.onboardingStatus || 'pending';
    const plan = profile?.plan || null;

    localStorage.setItem('onboardingStatus', onboardingStatus);
    if (plan) localStorage.setItem('plan', plan);
    persistEffectiveUserId(profile);

    setAuth({
      isHydrated: true,
      isLoggedIn: true,
      accessToken: tokens.accessToken,
      refreshToken: tokens.refreshToken,
      idToken: tokens.idToken,
      onboardingStatus,
      plan,
    });

    return onboardingStatus;
  }

  async function login(tokens) {
    return await hydrateWithTokens(tokens);
  }

  function setOnboardingStatus(status) {
    localStorage.setItem('onboardingStatus', status);
    setAuth((prev) => ({
      ...prev,
      onboardingStatus: status,
    }));
  }

  function setPlan(plan) {
    localStorage.setItem('plan', plan);
    setAuth((prev) => ({ ...prev, plan }));
  }

  async function logout() {
    try {
      await fetch(`${API_BASE}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    }

    localStorage.clear();
    setAuth({
      isHydrated: true,
      isLoggedIn: false,
      accessToken: null,
      refreshToken: null,
      idToken: null,
      onboardingStatus: null,
      plan: null,
    });
  }

  useEffect(() => {
    let active = true;

    async function hydrateFromRefreshCookie() {
      try {
        const username = localStorage.getItem('userEmail');
        // Same third-party-cookie problem as installAuthFetch: the refresh cookie
        // does not reach the API origin in every browser, so send our own copy.
        const storedRefreshToken = localStorage.getItem('refreshToken');
        const storedIdToken = localStorage.getItem('idToken');
        const refreshRes = await fetch(`${API_BASE}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            ...(username ? { username } : {}),
            ...(storedRefreshToken ? { refreshToken: storedRefreshToken } : {}),
            ...(storedIdToken ? { idToken: storedIdToken } : {}),
          }),
        });

        if (!refreshRes.ok) {
          // 401 means Cognito rejected the refresh token itself — it will never
          // succeed again. Drop the stale tokens so the route guards send them to
          // login, instead of every later 401 retrying a doomed refresh. Any
          // other status is transient and leaves the stored session intact.
          if (refreshRes.status === 401) {
            ['accessToken', 'idToken', 'refreshToken', 'expiresAt'].forEach((key) =>
              localStorage.removeItem(key)
            );
          }
          return;
        }

        const data = await refreshRes.json();

        if (!data?.ok || !data.accessToken || !data.idToken || !data.expiresIn) {
          return;
        }

        if (active) {
          await hydrateWithTokens(data);
        }
      } catch (err) {
        console.error('Session restore error:', err);
      }
    }

    async function initialize() {
      const savedOnboardingStatus = localStorage.getItem('onboardingStatus');
      if (savedOnboardingStatus) {
        setAuth((prev) => ({
          ...prev,
          onboardingStatus: savedOnboardingStatus,
        }));
      }

      const initial = getInitialAuth();
      if (initial.isLoggedIn && initial.idToken) {
        const profile = await fetchProfile(initial.idToken);
        if (isSuccessfulProfile(profile)) {
          if (profile.plan) localStorage.setItem('plan', profile.plan);
          persistEffectiveUserId(profile);
          setAuth((prev) => ({
            ...prev,
            isHydrated: true,
            isLoggedIn: initial.isLoggedIn,
            accessToken: initial.accessToken,
            refreshToken: initial.refreshToken,
            idToken: initial.idToken,
            onboardingStatus: profile.onboardingStatus,
            plan: profile.plan || prev.plan || null,
          }));
          return;
        }
      }

      await hydrateFromRefreshCookie();

      if (active) {
        const updated = getInitialAuth();
        setAuth((prev) => ({
          ...prev,
          isHydrated: true,
          isLoggedIn: updated.isLoggedIn,
          accessToken: updated.accessToken,
          refreshToken: updated.refreshToken,
          idToken: updated.idToken,
          onboardingStatus: updated.onboardingStatus,
        }));
      }
    }

    initialize();

    return () => {
      active = false;
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        auth,
        login,
        logout,
        setOnboardingStatus,
        setPlan,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
